"use client";

/**
 * https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr#a-quick-note-on-terminology
 */

import {
  QueryClient,
  QueryClientProvider as TanStackQueryClientProvider,
  isServer,
} from "tanstack-react-query-5";

const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // With SSR, we usually want to set some default staleTime
        // above 0 to avoid refetching immediately on the client
        staleTime: 60 * 1000,
      },
    },
  });
};

// eslint-disable-next-line no-undef-init
let browserQueryClient: QueryClient | undefined = undefined;

const getQueryClient = () => {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
};

interface QueryClientProvidersProps {
  children: React.ReactNode;
}
const QueryClientProvider = ({ children }: QueryClientProvidersProps) => {
  const queryClient = getQueryClient();

  return (
    <TanStackQueryClientProvider client={queryClient}>
      {children}
    </TanStackQueryClientProvider>
  );
};

export { QueryClientProvider };
